import { useFormContext } from 'react-hook-form';
import { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '../../../components/shadcn/button';
import { CrmItemViewModel, DisplayStyleType, FilterType, ReportData } from '../../../types/domain';
import { SelectOption } from '../../../components/common/Dropdown/types';
import { CrmLibraryItemGridPreview } from './CrmLibraryItemGridPreview';
import { CrmLibraryItemFunnelPreview } from './CrmLibraryItemFunnelPreview';
import { getReport } from '../../../api/libraryItem/api';
import { LoadingPage } from '@/components/common/Loading/LoadingPage';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRefresh } from '@fortawesome/free-solid-svg-icons';

type CrmLibraryItemPreviewProps = {
  dropdownOptions?: {
    groupOptions: SelectOption[];
    visibleColumnsOptions: SelectOption[];
    sortColumnOptions: SelectOption[];
    displayByOptions: SelectOption[];
    libraryItemTypeOptions: SelectOption[];
    displayStyleOptions: SelectOption[];
    dateFilterModeOptions: SelectOption[];
    pipelineOptions: SelectOption[];
    availablePipelineStages: SelectOption[];
  };
  availableFilters?: Array<{
    id: string;
    name: string;
    filterType: FilterType;
    values?: SelectOption[];
  }>;
  multiSelectSelectedOptions?: Record<string, SelectOption[]>;
}

export const CrmLibraryItemPreview = ({ dropdownOptions, availableFilters, multiSelectSelectedOptions = {} }: CrmLibraryItemPreviewProps) => {
  const { watch, getValues } = useFormContext<CrmItemViewModel>();
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const libraryItemType = watch('libraryItemType');
  const name = watch('name');
  const group = watch('group');
  const noResult = watch('noResult');
  const currentUserOnly = watch('currentUserOnly');
  const dateFilterMode = watch('dateFilterMode');
  const visibleColumns = watch('visibleColumns') || [];
  const displayBy = watch('displayBy');
  const displayStyle = watch('displayStyle');
  const sortColumn = watch('sortColumn');
  const sortDesc = watch('sortDesc');
  const dynamicFilters = watch('dynamicFilters') || [];
  const pipelineId = watch('pipelineId');
  const pipelineStages = watch('pipelineStages');
  const theme = watch('theme');

  const groupLabel = dropdownOptions?.groupOptions.find(opt => opt.value === group)?.label || group || '';
  const sortColumnLabel = dropdownOptions?.sortColumnOptions.find(opt => opt.value === sortColumn)?.label || sortColumn || '';
  const displayByLabel = dropdownOptions?.displayByOptions.find(opt => opt.value === displayBy)?.label || displayBy || '';
  const libraryItemTypeLabel = dropdownOptions?.libraryItemTypeOptions.find(opt => opt.value === libraryItemType)?.label || libraryItemType || 'Unknown';
  const displayStyleLabel = dropdownOptions?.displayStyleOptions.find(opt => opt.value === displayStyle)?.label || displayStyle || 'Not Selected';
  const dateFilterModeLabel = dropdownOptions?.dateFilterModeOptions.find(opt => opt.value === dateFilterMode)?.label || dateFilterMode || 'Not Selected';
  const pipelineLabel = dropdownOptions?.pipelineOptions.find(opt => opt.value === pipelineId)?.label || pipelineId || 'Not Selected';

  const getPipelineStagesDisplayText = () => {
    const selectedStages = pipelineStages || [];
    const availableStages = dropdownOptions?.availablePipelineStages || [];

    if (selectedStages.length === 0) {
      return 'None selected';
    }

    if (selectedStages.length === availableStages.length && availableStages.length > 0) {
      return 'All';
    }

    return `${selectedStages.length} stage${selectedStages.length === 1 ? '' : 's'}`;
  };

  const handleRefresh = async () => {
    try {
      setIsLoading(true);
      const currentFormData = getValues();
      const response = await getReport(currentFormData);

      if (response && response.columns && response.rows) {
        setReportData({
          columns: response.columns,
          rows: response.rows
        });
      }
    } catch (error) {
      console.error('Error calling GetReport:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Debounced auto-refresh on form changes
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const debouncedRefresh = useCallback(() => {
    // Show loader immediately, before debounce
    setIsLoading(true);

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      handleRefresh();
    }, 2000); // 2 second delay
  }, []);

  // get filters with actual values
  const getFiltersWithValues = useCallback(() => {
    return (dynamicFilters || []).filter((filter: any) => {
      return filter.values && filter.values.length > 0 && filter.values.some((value: string) => value && value.trim() !== '');
    });
  }, [dynamicFilters]);

  // Create a stable reference for filters with values
  // Only refresh preview when filters with values change
  const filtersWithValues = getFiltersWithValues();
  const filtersWithValuesHash = JSON.stringify(filtersWithValues.map((f: any) => ({ filterId: f.filterId, values: f.values })));

  // Watch for form changes and trigger debounced refresh
  useEffect(() => {
    if (pipelineId && visibleColumns && visibleColumns.length > 0) {
      debouncedRefresh();
    }

    // Cleanup timeout on unmount
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [pipelineId, pipelineStages, visibleColumns, displayBy, displayStyle, sortColumn, sortDesc, currentUserOnly, noResult, dateFilterMode, filtersWithValuesHash]);

  // format the dynamic filters into readable text
  const formatDynamicFiltersText = () => {
    if (!dynamicFilters || dynamicFilters.length === 0) {
      return 'No filters applied';
    }

    const filterDescriptions = dynamicFilters.map((filter: any) => {
      const filterConfig = availableFilters?.find(f => f.id === filter.filterId);
      if (!filterConfig || !filter.values || filter.values.length === 0) {
        return null;
      }

      const filterName = filterConfig.name.toLowerCase();
      const values = filter.values;

      if (values.length === 0) return null;

      switch (filterConfig.filterType) {
        case FilterType.MultiSelect: {
          // For multiselect, use the selectedOptions from parent state for accurate labels
          const selectedOptions = multiSelectSelectedOptions[filter.filterId] || [];
          const labels = values.map((value: string) => {
            const selectedOption = selectedOptions.find(opt => opt.value === value);
            if (selectedOption) return selectedOption.label;

            const configOption = filterConfig.values?.find(opt => opt.value === value);
            return configOption ? configOption.label : value;
          });

          if (labels.length === 1) {
            return `${filterName} is "${labels[0]}"`;
          } else if (labels.length === 2) {
            return `${filterName} is either "${labels[0]}" or "${labels[1]}"`;
          } else {
            const lastLabel = labels.pop();
            return `${filterName} is "${labels.join('", "')}" or "${lastLabel}"`;
          }
        }

        case FilterType.DropDown: {
          const labels = values.map((value: string) => {
            const option = filterConfig.values?.find(opt => opt.value === value);
            return option ? option.label : value;
          });

          if (labels.length === 1) {
            return `${filterName} is "${labels[0]}"`;
          } else if (labels.length === 2) {
            return `${filterName} is either "${labels[0]}" or "${labels[1]}"`;
          } else {
            const lastLabel = labels.pop();
            return `${filterName} is "${labels.join('", "')}" or "${lastLabel}"`;
          }
        }

        case FilterType.TextBox: {
          if (values[0] && values[0].trim() !== '') {
            return `${filterName} contains "${values[0]}"`;
          }
          return null;
        }

        case FilterType.DateRange: {
          const fromDate = values[0];
          const toDate = values[1];

          if (fromDate && toDate) {
            return `${filterName} is between ${new Date(fromDate).toLocaleDateString()} and ${new Date(toDate).toLocaleDateString()}`;
          } else if (fromDate) {
            return `${filterName} is after ${new Date(fromDate).toLocaleDateString()}`;
          } else if (toDate) {
            return `${filterName} is before ${new Date(toDate).toLocaleDateString()}`;
          }
          return null;
        }

        default:
          return `${filterName} is "${values[0]}"`;
      }
    }).filter(Boolean);

    if (filterDescriptions.length === 0) {
      return 'No filters applied';
    }

    return (
      <div>
        <div className="mb-2 text-gray-700">Data will be filtered to only include records that match ALL of the following conditions:</div>
        <ul className="list-disc list-inside space-y-1 ml-4">
          {filterDescriptions.map((description, index) => (
            <li key={index} className="text-gray-600">{description}</li>
          ))}
        </ul>
      </div>
    );
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Library Item Preview</h3>
        <Button
          type="button"
          variant="outline"
          onClick={handleRefresh}
          size="sm"
          className='text-kPurple-500'
        >
          Refresh Preview
          <FontAwesomeIcon icon={faRefresh} />
        </Button>
      </div>

      <div className="space-y-6">
        {/* Header */}
        <div className="border-b pb-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-xl font-bold text-gray-900">
                {name || 'Untitled Library Item'}
              </h4>
              {(groupLabel || group) && (
                <p className="text-sm text-gray-600 mt-1">Group: {groupLabel || group}</p>
              )}
            </div>
            <div className="text-right">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-gray-800">
                {libraryItemTypeLabel}
              </span>
            </div>
          </div>

          {/* Configuration Details */}
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium text-gray-600">Pipeline:</span>
                <span className="ml-2 text-gray-700">
                  {pipelineId !== undefined ? pipelineLabel : 'Not set'}
                </span>
              </div>
              <div>
                <span className="font-medium text-gray-600">Pipeline Stages:</span>
                <span className="ml-2 text-gray-700">
                  {getPipelineStagesDisplayText()}
                </span>
              </div>
              <div>
                <span className="font-medium text-gray-600">Display By:</span>
                <span className="ml-2 text-gray-700">
                  {displayByLabel}
                </span>
              </div>
              <div>
                <span className="font-medium text-gray-600">Data Scope:</span>
                <span className="ml-2 text-gray-700">
                  {currentUserOnly ? 'Current user data only' : 'All user data'}
                </span>
              </div>
              <div>
                <span className="font-medium text-gray-600">Save Dates As:</span>
                <span className="ml-2 text-gray-700">
                  {dateFilterMode !== undefined ? dateFilterModeLabel : 'Not set'}
                </span>
              </div>
              <div>
                <span className="font-medium text-gray-600">Display Style:</span>
                <span className="ml-2 text-gray-700">
                  {displayStyle !== undefined ? displayStyleLabel : 'Not set'}
                </span>
              </div>
              {displayStyle === DisplayStyleType[DisplayStyleType.dsGrid] && (
                <div>
                  <span className="font-medium text-gray-600">Sort By:</span>
                  <span className="ml-2 text-gray-700">
                    {sortColumnLabel !== undefined ? sortColumnLabel : 'Not set'}
                    {sortDesc ? ' (Descending)' : ' (Ascending)'}
                  </span>
                </div>
              )}
              {displayStyle === DisplayStyleType[DisplayStyleType.dsGrid] && (
                <div>
                  <span className="font-medium text-gray-600">Max Results:</span>
                  <span className="ml-2 text-gray-700">{noResult || 'Not set'}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Dynamic Filters Section */}
        <div>
          <h5 className="font-semibold text-gray-900 mb-3">Data Filters</h5>
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="text-sm text-gray-700">
              {formatDynamicFiltersText()}
            </div>
          </div>
        </div>

        {/* Data Preview - Grid or Funnel */}
        <div>
          <h5 className="font-semibold text-gray-900 mb-3">Data Preview</h5>

          {visibleColumns && visibleColumns.length > 0 ? (
            <div className="relative">
              {isLoading && (
                <LoadingPage message="Loading data..." />
              )}

              {(!isLoading && reportData) && (
                displayStyle === DisplayStyleType[DisplayStyleType.dsFunnelChart] ? (
                  <CrmLibraryItemFunnelPreview
                    visibleColumns={visibleColumns}
                    reportData={reportData}
                    pipelineLabel={pipelineLabel}
                    theme={theme}
                  />
                ) : (
                  <CrmLibraryItemGridPreview
                    visibleColumns={visibleColumns}
                    displayBy={displayBy}
                    displayByLabel={displayByLabel}
                    noResult={noResult}
                    sortColumnLabel={sortColumnLabel}
                    sortDesc={sortDesc}
                    dropdownOptions={dropdownOptions}
                    reportData={reportData}
                  />
                )
              )}
            </div>
          ) : (
            <div className="bg-gray-50 rounded-lg p-8 text-center">
              <p className="text-gray-500">
                No columns selected for preview. Please select columns to see a data preview.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="text-center pt-4 border-t">
          <p className="text-sm text-gray-500">
            This is a preview of how your library item will appear to users
          </p>
        </div>
      </div>
    </div>
  );
};
